import { resolveCachedItemForRender } from './find-cached-item';
import { hasCampaignPermission, inventoryField, itemsCol, layerEntriesOf, layerListEntryUuid, resolveNest } from '../shared/array-types';
import { actorCanReachTarget } from '../shared/proximity-browser';
import { renderInventoryControls, renderTrapControls, invokeObjectCardAction, isContainerArr, selectCardPictureUrl } from './render-object-card';
import { buildContainerLootGenHtml, ftWireContainerLootGen } from './container-loot-gen';
import { ftHurlObjectActions } from './hurl-object';
import type { SceneObjectAction } from '../encounter-creation-engine/scene-object-action-menu';

const noAction = (): void => {};
const host = window as Window & {
    ftGetActiveCasterId?: () => string;
    ftObjectAttackOptions?: (casterId: string) => Array<{ id: string; name: string }>;
    ftUseAbilityAtExplicitTarget?: (abilityId: string, targetId: string) => Promise<void>;
    ftThrowObjectPlan?: (inventoryId: string) => { ok: boolean; reason: string | null; rangeFt: number | null; weightLb: number; mode: 'throw' | 'draw' | 'telekinesis'; apCost: number; twoHanded: boolean; freeHand: boolean | null };
    ftObjectManipulationPlan?: (inventoryId: string, verb: 'push' | 'pull') => { ok: boolean; reason: string | null; pickupOnly?: boolean };
    ftObjectManipulationStart?: (inventoryId: string, verb: 'push' | 'pull') => boolean;
    ftLatchRadialActions?: (inventoryId: string) => SceneObjectAction[];
};
// Owner 2026-09-10: ANY inventory can be struck with a fist/natural weapon or the weapon in hand —
// Janus owns whether the swing lands, so the radial offers it on every object.
function attackActions(id: string, casterId: string): SceneObjectAction[] {
    const options = host.ftObjectAttackOptions?.(casterId) ?? [];
    if (options.length === 0) return [{ label: 'No weapon', icon: 'attack_ability', enabled: false,
        reason: 'This character has no weapon in hand and no natural weapon — use an ability with "Include objects"', run: noAction }];
    return options.map(option => ({ label: option.name, icon: 'attack_ability', run: () => {
        // A menu is a snapshot: never swing for a character that control has since moved to.
        if (host.ftGetActiveCasterId?.() !== casterId) return;
        void host.ftUseAbilityAtExplicitTarget?.(option.id, id);
    } }));
}
function controls(id: string, traps = false): HTMLElement | null {
    const row = resolveCachedItemForRender(id);
    if (!row || String(inventoryField(row, 'inventory_id') ?? '').toUpperCase() !== id.toUpperCase()) return null;
    const root = document.createElement('div');
    root.innerHTML = traps ? renderTrapControls(row, escapeHtml(id)) : renderInventoryControls(row, true);
    return root;
}
function isGm(id: string): boolean {
    const row = resolveCachedItemForRender(id);
    return !!row && hasCampaignPermission(row);
}
function command(id: string, label: string, icon: string, selector: string, gm = true, traps = false): SceneObjectAction | null {
    if ((gm && !isGm(id)) || !controls(id, traps)?.querySelector(selector)) return null;
    return { label, icon, run: () => {
        if (gm && !isGm(id)) return;
        const button = controls(id, traps)?.querySelector<HTMLElement>(selector);
        if (button) invokeObjectCardAction(button, new MouseEvent('click', { cancelable: true }));
    } };
}
function generatorForm(id: string, slug: string, label: string): void {
    if (!isGm(id)) return;
    document.querySelector<HTMLDialogElement>('[data-ft3d-generator-dialog]')?.close();
    const dialog = document.createElement('dialog'); dialog.dataset.ft3dGeneratorDialog = '';
    dialog.style.cssText = 'position:fixed;inset:0;margin:auto;height:fit-content;width:min(650px,calc(100vw - 24px));max-height:calc(100dvh - 24px);overflow:auto;background:#18292d;color:white;border:1px solid #b9a66b;border-radius:12px;padding:16px';
    dialog.setAttribute('aria-label', label);
    const title = document.createElement('h3'); title.textContent = label;
    const close = document.createElement('button'); close.type = 'button'; close.className = 'btn btn-secondary'; close.textContent = 'Close';
    close.addEventListener('click', () => dialog.close());
    const form = document.createElement('div'); form.innerHTML = buildContainerLootGenHtml(id);
    form.querySelector('.inventory-random-gen-header-toggle')?.remove();
    form.querySelector('.inventory-crg-tabstrip')?.remove();
    form.querySelectorAll<HTMLElement>('.tab-pane').forEach(pane => {
        if (!pane.id.startsWith(`crg_pane_${slug}_`)) pane.remove();
        else pane.classList.add('active', 'show');
    });
    dialog.append(title, form, close); document.body.append(dialog);
    dialog.addEventListener('close', () => { disposeBootstrapTooltipsWithin(dialog); dialog.remove(); }, { once: true });
    ftWireContainerLootGen(form);
    const collapse = form.querySelector('.collapse'); collapse?.classList.add('show');
    collapse?.dispatchEvent(new Event('shown.bs.collapse'));
    (window as Window & { initializeTomSelect?: () => void }).initializeTomSelect?.();
    dialog.showModal(); close.focus();
}
function generators(id: string): SceneObjectAction[] {
    if (!isGm(id)) return [];
    return ([
        ['scroll', 'Scrolls', 'scroll'], ['skill_book', 'Skill Books', 'book'],
        ['iconic_book', 'Iconic Books', 'inventory_item_id'], ['ammo', 'Ammunition', 'ammo'], ['gemstone', 'Gemstones', 'gemstone'],
    ] as [string, string, string][]).map(([slug, label, icon]) => ({ label, icon, run: () => generatorForm(id, slug, label) }));
}
function gmTools(id: string): SceneObjectAction[] {
    if (!isGm(id)) return [];
    const root = controls(id); if (!root) return [];
    const actions: SceneObjectAction[] = [];
    const add = (action: SceneObjectAction | null): void => { if (action) actions.push(action); };
    add(command(id, 'Edit', 'edit_inventory', '[data-ft-oc-action="open-edit-inventory"]'));
    const gallery = root.querySelector<HTMLAnchorElement>('a[id^="btn_media_manager_inventory_"]');
    if (gallery) actions.push({ label: 'Image Gallery', icon: 'modify_picture_gallery', run: () => { if (isGm(id)) window.location.assign(gallery.href); } });
    const shown = (selector: string): string | null => Array.from(root.querySelectorAll<HTMLElement>(selector)).find(el => el.style.display !== 'none')?.id ?? null;
    const visibility = shown('[id^="btn_reveal_creature_"], [id^="btn_hide_creature_"]');
    if (visibility) add(command(id, visibility.startsWith('btn_reveal') ? 'Reveal' : 'Hide', visibility.startsWith('btn_reveal') ? 'reveal' : 'hidden', `#${CSS.escape(visibility)}`));
    const privacy = shown('[id^="inventory_public_"], [id^="inventory_private_"]');
    if (privacy) add(command(id, privacy.startsWith('inventory_public') ? 'Make Private' : 'Make Public', privacy.startsWith('inventory_public') ? 'private' : 'public', `#${CSS.escape(privacy)}`));
    const rubble = command(id, 'Confirm Rubble', 'confirm', '[id^="btn_confirm_turn_rubble_inventory_"]');
    if (rubble) {
        // R1: warm the pile's definition now so Confirm converts client-side without a round trip.
        (window as Window & { ftPrefetchObjectDestruction?: (input: { inventoryId: string }) => void }).ftPrefetchObjectDestruction?.({ inventoryId: id });
        actions.push({ label: 'Turn to Rubble', icon: 'rubble', children: () => [rubble], run: noAction });
    }
    const deletion = command(id, 'Confirm Delete', 'delete_inventory', '[data-ft-oc-action="delete-inventory"]');
    if (deletion) actions.push({ label: 'Delete', icon: 'delete_inventory', children: () => [deletion], run: noAction });
    const row = resolveCachedItemForRender(id);
    if (row && isContainerArr(row)) actions.push({ label: 'Random Generators', icon: 'book', children: () => generators(id), run: noAction });
    return actions;
}
function inventoryRadialActions(id: string): SceneObjectAction[] {
    const actions: SceneObjectAction[] = [];
    const status = command(id, 'Status Effects', 'status_effects', '[data-ft-oc-action="open-inventory-status-effects"]', false);
    if (status) actions.push({ ...status, slot: -2 });
    if (isGm(id)) actions.push({ label: 'GM Tools', icon: 'gm', slot: -3, children: () => gmTools(id), run: noAction });
    if (resolveCachedItemForRender(id)) {
        const casterId = host.ftGetActiveCasterId?.() ?? '';
        actions.push(...(host.ftLatchRadialActions?.(id) ?? []));
        const item = resolveCachedItemForRender(id);
        if (itemsCol(item, 'item_type') === 'light_source') {
            const seen = new Set<string>();
            const abilities = ['full_ability','quick_ability','free_ability'].flatMap(layer => layerEntriesOf(item, layer))
                .flatMap(entry => {
                    const abilityId = layerListEntryUuid(entry);
                    if (!abilityId || seen.has(abilityId)) return [];
                    seen.add(abilityId);
                    const ability = resolveNest(entry, item);
                    return [{ label: itemsCol(ability, 'item_name') ?? 'Use light source', icon: 'light_source',
                        enabled: !!casterId && !!host.ftUseAbilityAtExplicitTarget,
                        reason: casterId ? null : 'Control a character first', run: () => {
                            if (host.ftGetActiveCasterId?.() !== casterId) return;
                            void actorCanReachTarget(casterId, id).then(within => {
                                if (host.ftGetActiveCasterId?.() !== casterId) return;
                                if (within !== true) { displayWarning(within === false ? 'Move within reach to use this light source.' : 'Could not determine reach.'); return; }
                                void host.ftUseAbilityAtExplicitTarget?.(abilityId, id);
                            });
                        } }];
                });
            if (abilities.length) actions.push({ label: 'Lighting', icon: 'light_source', children: () => abilities, run: noAction });
        }
        actions.push({ label: 'Attack', icon: 'attack_ability',
            enabled: casterId !== '' && typeof host.ftUseAbilityAtExplicitTarget === 'function',
            reason: casterId ? null : 'Control a character first',
            children: () => attackActions(id, casterId), run: noAction });
        // Carry verbs (owner 2026-09-15) — the object card renders the buttons; the radial reuses them so the
        // two surfaces cannot disagree. Throw arms the lobbed placement with THIS actor's computed range.
        const carry = (label: string, icon: string, selector: string, verdict: () => { enabled: boolean; reason: string | null }): void => {
            const action = command(id, label, icon, selector, false);
            if (!action) return;
            const { enabled, reason } = casterId ? verdict() : { enabled: false, reason: 'Control a character first' };
            actions.push({ ...action, enabled, reason });
        };
        // Pick up takes a free hand; Throw needs the object carried (in hand = a throw, otherwise drawn to a free hand first) — the
        // rules are janus-engine/actions/throw-object-gate.ts, read through ftThrowObjectPlan (owner 2026-09-16).
        carry('Pick up', 'pick_up_inventory', '[data-ft-oc-action="pick-up-inventory"]', () => host.ftThrowObjectPlan?.(id)?.freeHand === false
            ? { enabled: false, reason: 'Your hands are full — free a hand to pick that up.' }
            : { enabled: true, reason: 'Take it (1 AP in combat, must be within reach, needs a free hand)' });
        carry('Throw', 'throw_inventory', '[data-ft-oc-action="throw-inventory"]', () => {
            const plan = host.ftThrowObjectPlan?.(id);
            if (!plan) return { enabled: true, reason: null };
            if (!plan.ok) return { enabled: false, reason: plan.reason };
            const how = `${plan.mode === 'draw' ? 'drawn to a free hand, then ' : plan.mode === 'telekinesis' ? 'telekinesis: flies from where it lies, ' : ''}lobbed (L while aiming = straight)`;
            const bolts = plan.apCost > 0 ? ` · ${'⚡'.repeat(plan.apCost)}` : '';
            return { enabled: true, reason: `Range ${plan.rangeFt} ft · ${plan.weightLb} lb${plan.twoHanded ? ' · both hands' : ''} · ${how}${bolts}` };
        });
        carry('Drop', 'drop_inventory', '[data-ft-oc-action="drop-inventory"]', () => ({ enabled: true, reason: 'Set it down at your feet' }));
        const manipulate = (verb: 'push' | 'pull'): SceneObjectAction => {
            const plan = host.ftObjectManipulationPlan?.(id, verb);
            return { label: verb === 'push' ? 'Push' : 'Pull', icon: verb === 'push' ? 'push_party_to_encounter' : 'object_pull',
                enabled: plan?.ok === true, reason: plan?.reason ?? 'The manipulation gate is not ready.',
                run: () => { host.ftObjectManipulationStart?.(id, verb); } };
        };
        const moveActions = actions.filter(action => ['Pick up', 'Throw', 'Drop'].includes(action.label));
        for (const action of moveActions) actions.splice(actions.indexOf(action), 1);
        if (!host.ftObjectManipulationPlan?.(id, 'push')?.pickupOnly) moveActions.push(manipulate('push'), manipulate('pull'));
        if (moveActions.length) actions.push({ label: 'Move', icon: 'move_inventory', children: () => moveActions, run: noAction });
        // Telekinesis (2026-10-08): the active caster's hurl mentas — pick THIS object, then the menta's own targeting (hurl-object.ts).
        if (ftHurlObjectActions(id).length) actions.push({ label: 'Telekinesis', icon: 'hurls_unattended_object', children: () => ftHurlObjectActions(id), run: noAction });
    }
    const traps = (): SceneObjectAction[] => [
        command(id, 'Disarm Trap', 'trap', '[data-ft-oc-action="trap-disarm"]', false, true),
        command(id, 'Arm Trap', 'trap', '[data-ft-oc-action="trap-arm"]', false, true),
        command(id, 'Search Traps', 'search', '[data-ft-oc-action="trap-discern"]', false, true),
    ].filter((action): action is SceneObjectAction => action !== null).map(action => ({ ...action,
        enabled: !!host.ftGetActiveCasterId?.(), reason: host.ftGetActiveCasterId?.() ? null : 'Control a character first' }));
    if (traps().length) actions.push({ label: 'Traps', icon: 'trap', children: traps, run: noAction });
    return actions;
}
/**
 * Hub art for an inventory wheel, read from the LIVE cached row at open time — the same picture the
 * object card paints, so a re-uploaded portrait or a changed thumbnail pic shows on the next wheel
 * instead of the art the 3D projection snapshotted when the scene was built (owner 2026-09-15).
 */
function inventoryRadialThumbnail(id: string): { imageUrl: string | null } {
    const row = resolveCachedItemForRender(String(id ?? '').trim());
    const url = row ? selectCardPictureUrl(row) : '';
    return { imageUrl: /^https?:\/\//i.test(url) ? url : null };
}
Object.assign(window, { ftInventoryRadialActions: inventoryRadialActions, ftInventoryRadialThumbnail: inventoryRadialThumbnail });
